// Parser para factura PDF do fornecedor PLUS SKILL Ltd
// Stage A: extracao local com pdfjs-dist + regex baseado no template "OFFER"
//
// Outputs:
//   {
//     numero_fatura: string | null,         // ex "68"
//     data_fatura: 'YYYY-MM-DD' | null,
//     valor_total: number,                  // ex 6144.04
//     valor_sem_iva: number,                // (igual ao total em regime OSS)
//     linhas: [{
//       idx, ean, nome, qty,
//       preco_unit_bruto, desconto_pct,
//       valor_total, custo_unit_real
//     }],
//     raw_text: string,                     // amostra para debug
//     ok: boolean,
//     avisos: string[],
//   }

let _pdfjs = null
async function loadPdfjs() {
  if (_pdfjs) return _pdfjs
  // Import dinamico para nao engordar bundle inicial
  const lib = await import('pdfjs-dist')
  // Worker via URL (Vite resolve com ?url)
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  lib.GlobalWorkerOptions.workerSrc = workerUrl
  _pdfjs = lib
  return lib
}

/**
 * Extrai texto de um PDF (todas as paginas) preservando linhas pela posicao Y.
 * @param {File|Blob} file
 * @returns {Promise<string>}
 */
export async function extractTextFromPdf(file) {
  const pdfjs = await loadPdfjs()
  const arrayBuffer = await file.arrayBuffer()
  const doc = await pdfjs.getDocument({ data: arrayBuffer }).promise
  const linhasTotais = []
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i)
    const content = await page.getTextContent()
    // Agrupar items por Y proximo (tolerancia ~3 unidades)
    const items = content.items
      .map(it => ({ str: it.str, x: it.transform[4], y: Math.round(it.transform[5]) }))
      .filter(it => it.str && it.str.trim() !== '')
    // Ordenar primeiro por Y desc (PDF y cresce para cima), depois X asc
    items.sort((a, b) => (b.y - a.y) || (a.x - b.x))
    const linhas = []
    let actualY = null
    let actualLinha = []
    for (const it of items) {
      if (actualY === null || Math.abs(actualY - it.y) <= 3) {
        actualLinha.push(it)
        actualY = actualY === null ? it.y : actualY
      } else {
        // flush
        actualLinha.sort((a, b) => a.x - b.x)
        linhas.push(actualLinha.map(i => i.str).join(' '))
        actualLinha = [it]
        actualY = it.y
      }
    }
    if (actualLinha.length) {
      actualLinha.sort((a, b) => a.x - b.x)
      linhas.push(actualLinha.map(i => i.str).join(' '))
    }
    linhasTotais.push(...linhas)
  }
  return linhasTotais.join('\n')
}

/** Normaliza numero PT/EN: "6,144.04" / "6.144,04" -> 6144.04 */
function parseNumero(s) {
  if (!s) return 0
  let t = String(s).replace(/€|\s/g, '').trim()
  // Se tem tanto ',' como '.', assume formato US: "," milhar, "." decimal
  if (t.includes(',') && t.includes('.')) {
    if (t.lastIndexOf(',') < t.lastIndexOf('.')) {
      t = t.replace(/,/g, '')
    } else {
      t = t.replace(/\./g, '').replace(',', '.')
    }
  } else if (t.includes(',') && !t.includes('.')) {
    // pode ser decimal PT "169,65" ou milhar US "6,816"
    const partes = t.split(',')
    if (partes[1] && partes[1].length === 2) t = t.replace(',', '.')
    else t = t.replace(/,/g, '')
  }
  const n = parseFloat(t)
  return Number.isFinite(n) ? n : 0
}

/** Converte "14/05/2026" -> "2026-05-14" */
function parseDataDDMMYYYY(s) {
  if (!s) return null
  const m = String(s).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!m) return null
  return `${m[3]}-${m[2]}-${m[1]}`
}

/**
 * Parser para o formato "OFFER" da PLUS SKILL Ltd.
 * Devolve uma estrutura uniforme. Tolerante a falhas: se nao parsear algo, marca em `avisos`.
 */
export function parsePlusSkillOffer(rawText) {
  const avisos = []
  const text = (rawText || '').replace(/\r\n/g, '\n').replace(/ /g, ' ')

  // Numero da factura
  let numero_fatura = null
  const numMatch = text.match(/OFFER\s+(\d+)/i)
  if (numMatch) numero_fatura = numMatch[1]
  else avisos.push('Numero da factura nao detectado.')

  // Data
  let data_fatura = null
  const dataMatch = text.match(/DATE\s+(\d{2}\/\d{2}\/\d{4})/i)
  if (dataMatch) data_fatura = parseDataDDMMYYYY(dataMatch[1])
  else avisos.push('Data nao detectada.')

  // Total — pode aparecer como "TOTAL € 6,144.035" ou "TOTAL 6144.04"
  // PRIORIDADE 1: pattern explicito "TOTAL € X" no fim do PDF (mais robusto)
  // PRIORIDADE 2: ultima ocorrencia de TOTAL sem "Sub" antes
  // PRIORIDADE 3: linha "Subtotal X" no fim (factura reverse charge: TOTAL = Subtotal)
  let valor_total = 0
  const explicitTotal = text.match(/(?<![Ss]ub)TOTAL\s*€\s*([\d.,]+)/i)
  if (explicitTotal) {
    valor_total = parseNumero(explicitTotal[1])
  } else {
    const totalMatches = [...text.matchAll(/(?<![Ss]ub)TOTAL\b\s*€?\s*([\d.,]+)/gi)]
    if (totalMatches.length > 0) {
      valor_total = parseNumero(totalMatches[totalMatches.length - 1][1])
    }
  }
  // Fallback: se nao apanhou ou se o valor parece muito alto vs subtotal, usar subtotal
  const subtotalMatch = text.match(/Subtotal\s*([\d.,]+)/i)
  if (subtotalMatch) {
    const sub = parseNumero(subtotalMatch[1])
    if (!valor_total || (sub > 0 && valor_total > sub * 1.15)) {
      // TOTAL detectado e muito acima do subtotal — provavelmente apanhou valor errado (qty bruta?)
      // Em facturas Plus Skill reverse charge: TOTAL == Subtotal liquido
      valor_total = sub
    }
  }
  if (!valor_total) avisos.push('Total nao detectado.')

  // OSS — valor sem IVA = total (reverse charge BG->PT, IVA 0%)
  const valor_sem_iva = valor_total

  // Linhas de produtos — suporta DOIS layouts:
  //
  // LAYOUT A (antigo): cada item e um BLOCO com nome + precos juntos:
  //   "N. EAN: 13digits ProductName unit_price qty 0 bruto € disc% disc_amount € liquido €"
  //   Ordem cols: unit_price | qty | 0 | bruto € | disc% | desc_amount € | liquido €
  //
  // LAYOUT B (novo, factura 84+): tabela de precos no topo + descricoes EAN no fim:
  //   Tabela: " qty 0 bruto € unit_price disc% liquido € desc_amount €"
  //   Descricoes: "1.EAN: ... ProductName", "2.EAN: ...", ...
  //
  // Estrategia: tentar layout A (priceRowRegex dentro do bloco EAN).
  // Se falhar (qty=0), tentar layout B: apanhar todas as price rows do topo
  // separadamente e fazer match por INDICE com as descricoes EAN.
  const linhas = []
  const itemRegex = /(\d+)\s*\.\s*EAN:\s*(\d{8,14})([\s\S]*?)(?=\n?\s*\d+\s*\.\s*EAN:|$)/gi
  // priceRowRegexA (layout antigo): unit_price | qty | 0 | bruto € | disc% | desc_amount € | liquido €
  const priceRowRegexA = /([\d.,]+)\s+(\d+)\s+0\s+([\d.,]+)\s*€\s*([\d.,]+)\s*%\s*([\d.,]+)\s*€\s*([\d.,]+)\s*€/
  // priceRowRegexB (layout novo, factura 84+): qty | 0 | bruto € | unit_price | disc% | liquido € | desc_amount €
  const priceRowRegexB = /(\d+)\s+0\s+([\d.,]+)\s*€\s+([\d.,]+)\s+([\d.,]+)\s*%\s+([\d.,]+)\s*€\s+([\d.,]+)\s*€/g

  // Extrair descricoes EAN primeiro (layout A inclui price, layout B apenas nome)
  const itensRaw = []
  let m
  while ((m = itemRegex.exec(text)) !== null) {
    itensRaw.push({ idx: parseInt(m[1], 10), ean: m[2], block: m[3] || '' })
  }

  // Layout A: tentar matchar precos dentro de cada bloco EAN
  let layoutADetectado = 0
  const itensLayoutA = itensRaw.map(it => {
    const pm = it.block.match(priceRowRegexA)
    if (!pm) return { ...it, qty: 0 }
    layoutADetectado++
    const unit_price = parseNumero(pm[1])
    const qty = parseInt(pm[2], 10)
    const total_bruto = parseNumero(pm[3])
    const desc_pct = parseNumero(pm[4])
    const desc_amount = parseNumero(pm[5])
    const total_liquido = parseNumero(pm[6])
    const nome = it.block.replace(priceRowRegexA, ' ').replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim()
    return { ...it, nome, qty, unit_price, total_bruto, desc_pct, desc_amount, total_liquido }
  })

  if (layoutADetectado >= Math.ceil(itensRaw.length * 0.5)) {
    // Layout A apanhou metade ou mais — usar
    for (const it of itensLayoutA) {
      linhas.push({
        idx: it.idx, ean: it.ean, nome: it.nome || '',
        qty: it.qty || 0,
        preco_unit_bruto: it.unit_price || 0,
        desconto_pct: it.desc_pct || 0,
        valor_total: it.total_liquido || 0,
        desconto_valor: it.desc_amount || 0,
        total_bruto: it.total_bruto || 0,
        custo_unit_real: it.qty > 0 ? Number(((it.total_liquido || 0) / it.qty).toFixed(4)) : 0,
      })
    }
  } else {
    // Layout B: apanhar todas as price rows no texto na ordem em que aparecem
    const priceRows = []
    let pm2
    while ((pm2 = priceRowRegexB.exec(text)) !== null) {
      priceRows.push({
        qty: parseInt(pm2[1], 10),
        total_bruto: parseNumero(pm2[2]),
        unit_price: parseNumero(pm2[3]),
        desc_pct: parseNumero(pm2[4]),
        total_liquido: parseNumero(pm2[5]),
        desc_amount: parseNumero(pm2[6]),
      })
    }
    // Match por indice (1ª descricao = 1ª price row, etc.)
    for (let i = 0; i < itensRaw.length; i++) {
      const it = itensRaw[i]
      const pr = priceRows[i]
      const nome = (it.block || '').replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim()
      if (pr) {
        linhas.push({
          idx: it.idx, ean: it.ean, nome,
          qty: pr.qty,
          preco_unit_bruto: pr.unit_price,
          desconto_pct: pr.desc_pct,
          valor_total: pr.total_liquido,
          desconto_valor: pr.desc_amount,
          total_bruto: pr.total_bruto,
          custo_unit_real: pr.qty > 0 ? Number((pr.total_liquido / pr.qty).toFixed(4)) : 0,
        })
      } else {
        linhas.push({
          idx: it.idx, ean: it.ean, nome,
          qty: 0, preco_unit_bruto: 0, desconto_pct: 0,
          valor_total: 0, desconto_valor: 0, total_bruto: 0, custo_unit_real: 0,
        })
      }
    }
    if (priceRows.length > 0 && priceRows.length !== itensRaw.length) {
      avisos.push(`Layout novo detectado: ${priceRows.length} linhas de preco vs ${itensRaw.length} descricoes EAN — verifica o mapeamento.`)
    }
  }
  if (linhas.length === 0) avisos.push('Nenhuma linha de produto detectada. Confirma o texto bruto em baixo.')
  const semPreco = linhas.filter(l => l.qty === 0).length
  if (semPreco > 0) avisos.push(`${semPreco} de ${linhas.length} linha(s) sem preco detectado.`)

  // Sanity check: somatorio das linhas deve bater com valor_total (margem 2 EUR p/ arredondamentos)
  const somaLinhas = linhas.reduce((s, l) => s + l.valor_total, 0)
  if (valor_total > 0 && Math.abs(somaLinhas - valor_total) > 2) {
    avisos.push(`Soma das linhas (${somaLinhas.toFixed(2)}) difere do total (${valor_total.toFixed(2)}) por ${Math.abs(somaLinhas - valor_total).toFixed(2)} €.`)
  }

  const ok = !!numero_fatura && !!data_fatura && valor_total > 0 && linhas.length > 0
  return {
    numero_fatura, data_fatura, valor_total, valor_sem_iva,
    linhas, ok, avisos,
    fornecedor: 'PLUS SKILL',
    raw_text: text.slice(0, 3500),
    debug: {
      linhasFound: linhas.length,
      linhasComPreco: linhas.filter(l => l.qty > 0).length,
    },
  }
}

/** Pipeline completo: file PDF -> dados estruturados */
/**
 * Parser da NAYPES FRAGANCIAS Y COSMETICOS, S.L.
 *
 * Formato muito mais simples do que o da Plus Skill: uma tabela com
 *   Producto | EAN | Cantidad | Precio venta | Importe
 * e o nome do produto pode partir-se em duas linhas, com o EAN a aparecer
 * so na linha onde estao os numeros. Por isso ancora-se no EAN — treze
 * digitos — e le-se o que vem a seguir.
 *
 * Particularidades desta factura, todas com consequencia:
 *
 *   TRANSPORTE — vem como linha, sem EAN. Nao e um artigo e nao pode entrar
 *   nas linhas de custo, senao ficava um "produto" que nao existe no catalogo.
 *   Fica a parte, em `portes`, para o ecra o poder mostrar.
 *
 *   IVA zero — e uma aquisicao intracomunitaria com autoliquidacao. O
 *   "Total EUR Incl. VAT" e igual ao "Total EUR". Nao ha IVA para somar.
 *
 *   O documento pode ser Pro Forma (PEDV26-...) ou factura. O numero apanha-se
 *   de qualquer um dos dois; muda o prefixo, nao o formato.
 */
export function parseNaypes(rawText) {
  const avisos = []
  const text = (rawText || '').replace(/\r\n/g, '\n').replace(/ /g, ' ')

  // Numero: "PEDV26-010554" na proforma, ou o numero da factura definitiva.
  let numero_fatura = null
  const mNum = text.match(/\b(PEDV\d{2}-\d+|FAV\d{2}-\d+|[A-Z]{2,4}\d{2}-\d{4,})\b/)
  if (mNum) numero_fatura = mNum[1]
  else avisos.push('Numero do documento nao detectado.')

  // Data por extenso em espanhol: "17 de agosto de 2026"
  const MESES_ES = {
    enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6,
    julio: 7, agosto: 8, septiembre: 9, setiembre: 9, octubre: 10,
    noviembre: 11, diciembre: 12,
  }
  let data_fatura = null
  const mData = text.match(/(\d{1,2})\s+de\s+([a-zç]+)\s+de\s+(\d{4})/i)
  if (mData) {
    const mes = MESES_ES[mData[2].toLowerCase()]
    if (mes) data_fatura = `${mData[3]}-${String(mes).padStart(2, '0')}-${String(mData[1]).padStart(2, '0')}`
  }
  if (!data_fatura) {
    const mDataNum = text.match(/(\d{2})\/(\d{2})\/(\d{4})/)
    if (mDataNum) data_fatura = `${mDataNum[3]}-${mDataNum[2]}-${mDataNum[1]}`
  }
  if (!data_fatura) avisos.push('Data nao detectada.')

  // Total: "Total EUR Incl. VAT 150,11" — e se nao houver, "Total EUR 150,11".
  let valor_total = 0
  const mTotalIva = text.match(/Total\s+EUR\s+Incl\.?\s*VAT\s+([\d.,]+)/i)
  const mTotal = text.match(/Total\s+EUR\s+([\d.,]+)/i)
  if (mTotalIva) valor_total = parseNumero(mTotalIva[1])
  else if (mTotal) valor_total = parseNumero(mTotal[1])
  else avisos.push('Total nao detectado.')

  // Linhas: ancoradas no EAN de 13 digitos, seguido de qtd, preco e importe.
  // O nome vem ANTES do EAN e pode ter mudado de linha — apanha-se o que
  // estiver entre o fim da linha anterior e o EAN.
  const linhas = []
  // O PONTO COLADO AO EAN. O Naypes manda, de vez em quando, um EAN com um
  // ponto final agarrado — "3614273844673." — e o padrao exigia espaco logo a
  // seguir aos treze digitos. A linha inteira caia fora, em silencio.
  //
  // A 14/09/2026 foi assim que a proforma PEDV26-011841 chegou: nove linhas no
  // PDF, oito lidas, e 98,85 EUR de G. Armani My Way a faltar. O aviso da
  // diferenca apanhou-o — mas so porque alguem leu o aviso.
  //
  // Aceita-se um ponto ou uma virgula a seguir ao EAN e ignora-se.
  const RE_LINHA = /([A-ZÁÉÍÓÚÑÜ0-9][^\n]{2,120}?)\s+(\d{13})[.,]?\s+(\d+)\s+([\d.,]+)\s+([\d.,]+)/gi
  let m
  while ((m = RE_LINHA.exec(text)) !== null) {
    const nome = m[1].replace(/\s+/g, ' ').trim()
    const ean = m[2]
    const qty = parseInt(m[3], 10)
    const unit = parseNumero(m[4])
    const total = parseNumero(m[5])
    if (!(qty > 0)) continue
    linhas.push({
      idx: linhas.length + 1,
      ean, nome,
      qty,
      preco_unit_bruto: unit,
      desconto_pct: 0,
      desconto_valor: 0,
      total_bruto: total,
      valor_total: total,
      // O custo por unidade e o preco de venda do fornecedor. Nao ha desconto
      // nesta factura — o Naypes vende a tabela, e e por isso que o
      // desconto_supplier_pct destes artigos e zero.
      custo_unit_real: qty > 0 ? Number((total / qty).toFixed(4)) : 0,
    })
  }

  // TRANSPORTE: linha sem EAN, com dois numeros iguais no fim.
  let portes = 0
  const mPortes = text.match(/TRANSPORTE\s+([\d.,]+)\s+([\d.,]+)/i)
  if (mPortes) portes = parseNumero(mPortes[2])

  if (linhas.length === 0) avisos.push('Nenhuma linha de produto detectada.')

  // Conferencia: linhas + portes tem de dar o total impresso. E o que separa
  // "li o ficheiro" de "li o ficheiro todo".
  const somaLinhas = linhas.reduce((s, l) => s + l.valor_total, 0)
  const somaComPortes = Math.round((somaLinhas + portes) * 100) / 100
  if (valor_total > 0 && Math.abs(somaComPortes - valor_total) > 0.05) {
    avisos.push(`Soma das linhas + transporte (${somaComPortes.toFixed(2)} €) difere do total impresso (${valor_total.toFixed(2)} €) por ${Math.abs(somaComPortes - valor_total).toFixed(2)} €.`)
  }

  if (/Pro\s*Forma/i.test(text)) {
    avisos.push('Este documento é uma PRO FORMA, não a factura definitiva. Confere o número quando a factura chegar.')
  }

  const ok = !!numero_fatura && !!data_fatura && valor_total > 0 && linhas.length > 0
  return {
    numero_fatura, data_fatura, valor_total,
    // Intracomunitaria com autoliquidacao: sem IVA, o total e a base.
    valor_sem_iva: valor_total,
    portes,
    linhas, ok, avisos,
    fornecedor: 'Naypes',
    raw_text: text.slice(0, 3500),
    debug: { linhasFound: linhas.length, portes },
  }
}

/** Pipeline completo: file PDF -> dados estruturados */
export async function parseFaturaPdfFile(file) {
  try {
    const text = await extractTextFromPdf(file)
    // Escolhe o leitor pelo que o proprio documento diz ser. Tentar os dois as
    // cegas e ficar com o que "parecer melhor" da resultados errados em
    // silencio no dia em que um formato mudar.
    if (/NAYPES/i.test(text)) return parseNaypes(text)
    return parsePlusSkillOffer(text)
  } catch (e) {
    return { ok: false, avisos: ['Falha ao ler PDF: ' + (e?.message || String(e))], linhas: [], raw_text: '' }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Comprovativo de transferencia do cliente.
//
// O `pago_1a_valor` era escrito a mao, e quando ficava por escrever o sistema
// caia numa cadeia de palpites que acabava em "metade". Numa encomenda de
// 4.652 EUR isso sao 2.326 EUR dados como recebidos sem ninguem ter confirmado.
//
// O valor esta no comprovativo, que o cliente ja carregou. Le-se de la.
//
// NUNCA GRAVA SOZINHO. Mostra o que leu e o admin confirma — e dinheiro, e um
// parser que se engana em silencio e pior do que nao haver parser nenhum.
// Se nao conseguir ler (print de telemovel, outro banco, PDF de imagem), diz
// que nao conseguiu e o fluxo antigo continua igual.
// ─────────────────────────────────────────────────────────────────────────────

// String.raw de proposito: escrito como '\d{1,3}...' o JavaScript le \d como um
// "d" solto — a expressao compila, nao da erro nenhum, e nunca encontra um
// numero. Foi assim que este leitor nasceu partido e so se percebeu ao testa-lo
// com um comprovativo a serio.
// So o ponto separa milhares. O espaco NAO, apesar de existir essa notacao:
// o extractor junta as colunas de uma linha com espacos, e um IBAN escrito em
// grupos de quatro seguido do valor lia-se como um numero unico — foi assim
// que "374,62" saiu como "7 374,62". Um leitor que acrescenta um algarismo ao
// pagamento e pior do que nao haver leitor nenhum.
const N_PT = String.raw`\d{1,3}(?:\.\d{3})*,\d{2}`

function numeroPt(s) {
  if (!s) return null
  const n = Number(String(s).replace(/[.\s]/g, '').replace(',', '.'))
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null
}

export function parseComprovativo(rawText) {
  const text = String(rawText || '').replace(/\u00a0/g, ' ')
  const avisos = []

  // Etiquetas por ordem de confianca. "Montante" e a do ActivoBank; as outras
  // sao dos bancos que aparecem de vez em quando. "Total de Comissoes" tem de
  // ficar de fora — vem logo a seguir ao montante e costuma ser 0,00.
  // `[^0-9]{0,20}` entre a etiqueta e o numero, em vez de exigir um espaco.
  // O extractor de PDF junta as celulas de uma linha e nem sempre poe separador
  // — a etiqueta pode vir colada ao valor, ou com dois pontos, ou com "EUR" no
  // meio, ou com um espaco duro. Exigir um espaco fazia falhar comprovativos
  // que tem o montante bem visivel.
  const etiquetas = [
    /Montante[^0-9]{0,20}(N)/i,
    /Valor\s+da\s+(?:transfer[eê]ncia|opera[çc][ãa]o)[^0-9]{0,20}(N)/i,
    /Valor\s+transferido[^0-9]{0,20}(N)/i,
    /Import[âa]ncia[^0-9]{0,20}(N)/i,
    /\bValor\b[^0-9]{0,20}(N)/i,
  ]
  let valor = null, etiqueta = null
  for (const re of etiquetas) {
    const m = text.match(new RegExp(re.source.replace('(N)', `(${N_PT})`), re.flags))
    if (m) { valor = numeroPt(m[1]); etiqueta = m[0].split(/[:\s]/)[0]; break }
  }

  // SEM ETIQUETA, PROCURA OS NUMEROS.
  //
  // Nem todos os bancos dao o mesmo documento: o "Comprovativo de Operacao" do
  // ActivoBank tem "Montante", mas o extracto de movimentos do mesmo banco nao
  // tem etiqueta nenhuma perto do valor. Depender da etiqueta deixava metade
  // dos comprovativos de fora.
  //
  // Recolhem-se os numeros em formato portugues, tiram-se os zeros (comissoes,
  // imposto de selo) e os que parecem datas ou IBANs. Se sobrar UM, e esse — e
  // diz-se que foi encontrado sem etiqueta. Se sobrarem varios, nao se escolhe:
  // mostram-se os candidatos e a pessoa aponta. Adivinhar qual dos numeros de
  // um extracto e o pagamento e a forma de escrever o valor errado com ar de
  // certo.
  // O numero tem de comecar e acabar sozinho. Sem estas fronteiras, um digito
  // que venha colado antes — de um IBAN, de uma referencia — entra no numero:
  // num extracto real leu-se "7374,62" onde o valor era "374,62". Um leitor que
  // acrescenta um algarismo ao pagamento e pior do que nao haver leitor.
  const N_SOLTO = new RegExp(String.raw`(?:^|[^\d.,])(` + N_PT + String.raw`)(?![\d])`, 'g')
  const candidatos = [...new Set(
    [...text.matchAll(N_SOLTO)]
      .map(m => numeroPt(m[1]))
      .filter(v => v != null && v >= 0.5)
  )].sort((a, b) => b - a)

  if (valor == null && candidatos.length === 1) {
    valor = candidatos[0]
    etiqueta = null
    avisos.push('Valor encontrado sem etiqueta — confirma antes de usar.')
  }
  if (valor == null) {
    avisos.push(candidatos.length > 1
      ? 'Varios numeros no documento: ' + candidatos.map(v => v.toFixed(2)).join(', ')
      : 'Nao encontrei nenhum valor no comprovativo.')
  }

  // Data: dd-mm-aaaa ou dd/mm/aaaa. A primeira que aparecer serve — nos
  // comprovativos e sempre a da operacao.
  let data = null
  const md = text.match(/(\d{2})[-/](\d{2})[-/](\d{4})/)
  if (md) data = `${md[3]}-${md[2]}-${md[1]}`

  // Banco e beneficiario, so para o admin reconhecer o documento de relance.
  const banco = /ActivoBank/i.test(text) ? 'ActivoBank'
              : /Millennium/i.test(text) ? 'Millennium'
              : /Caixa\s*Geral|CGD/i.test(text) ? 'CGD'
              : /Santander/i.test(text) ? 'Santander'
              : /Novo\s*Banco/i.test(text) ? 'Novo Banco'
              : /Revolut/i.test(text) ? 'Revolut'
              : null
  const mb = text.match(/Nome do benefici[áa]rio\s*[:\s]\s*([A-ZÁÂÃÀÉÊÍÓÔÕÚÇ][A-ZÁÂÃÀÉÊÍÓÔÕÚÇ \-']{4,60})/i)

  return {
    ok: valor != null,
    valor, data, banco, etiqueta, candidatos,
    semEtiqueta: valor != null && etiqueta == null,
    beneficiario: mb ? mb[1].trim() : null,
    avisos,
    // Quando falha, o que interessa ver e o pedaco a VOLTA da etiqueta, nao o
    // principio do documento. O principio e sempre o cabecalho do banco e nao
    // diz nada sobre porque e que o numero nao foi encontrado.
    raw_text: (() => {
      const i = text.search(/Montante|Valor|Import[âa]ncia/i)
      return i >= 0 ? text.slice(Math.max(0, i - 20), i + 140) : text.slice(0, 200)
    })(),
  }
}

/** Pipeline: ficheiro (ou Blob) do comprovativo -> valor lido */
export async function parseComprovativoFile(file) {
  try {
    const text = await extractTextFromPdf(file)
    return parseComprovativo(text)
  } catch (e) {
    return { ok: false, valor: null, avisos: ['Nao consegui ler o PDF: ' + (e?.message || String(e))] }
  }
}

// ── SALDO NO MARKETPLACE ────────────────────────────────────────────────────
//
// O painel do marketplace mostra tres cartoes: o total, o que esta pendente de
// cativacao e o que ja esta disponivel para pedir. Escrever os tres a mao todos
// os dias e trabalho que se faz mal ao terceiro dia; colar o texto do painel
// faz-se sempre.
//
// A ARMADILHA ESTA NA LEGENDA DO TOTAL. Por baixo do total o painel escreve
// "Pendente + Disponivel + Solicitado" — as tres palavras que sao tambem as
// etiquetas dos cartoes, pela mesma ordem, e imediatamente ANTES dos valores.
// Procurar "Solicitado" seguido de um numero encontrava essa legenda e trazia o
// valor do PENDENTE, que vem logo a seguir. A legenda apaga-se antes de se
// procurar seja o que for.
//
// O TOTAL SERVE PARA CONFERIR, NAO PARA GRAVAR. Grava-se a soma das parcelas,
// porque e a soma que tem de bater. Se o total declarado nao bater com ela, foi
// lida uma parcela a menos — e isso diz-se, em vez de se gravar em silencio um
// numero que parece bom.
// O painel do marketplace escreve "2630,07" e nao "2.630,07": nao poe ponto
// nos milhares. O N_PT do resto do ficheiro exige esse ponto, e por isso lia o
// disponivel (231,19) mas nunca o pendente — ficava um saldo com metade do
// valor e sem nada a dizer que faltava alguma coisa. Aqui o ponto e opcional.
const N_MKT = String.raw`\d{1,3}(?:\.?\d{3})*,\d{2}`
export function parseSaldoMarketplace(rawText) {
  const texto = String(rawText || '').replace(new RegExp(String.fromCharCode(160), 'g'), ' ')
  const avisos = []

  // O total apanha-se ANTES de o apagar, porque e com ele que se confere.
  const mTotal = texto.match(new RegExp(String.raw`Saldo\s+total[^0-9]{0,20}(` + N_MKT + ')', 'i'))
  const totalDeclarado = mTotal ? numeroPt(mTotal[1]) : null

  const limpo = texto
    .replace(/Pendente\s*\+\s*Dispon[ií]vel\s*\+\s*Solicitado/gi, ' ')
    .replace(new RegExp(String.raw`Saldo\s+total[^0-9]{0,20}` + N_MKT, 'gi'), ' ')

  const apanha = (etiqueta) => {
    const m = limpo.match(new RegExp(etiqueta + String.raw`[^0-9]{0,20}(` + N_MKT + ')', 'i'))
    return m ? numeroPt(m[1]) : null
  }

  const pendente = apanha(String.raw`\bPendente\b`)
  const disponivel = apanha(String.raw`\bDispon[ií]vel\b`)
  const solicitado = apanha(String.raw`\bSolicitado\b`)

  if (pendente == null && disponivel == null && solicitado == null) {
    return {
      ok: false, pendente: 0, disponivel: 0, solicitado: 0, total: 0, totalDeclarado,
      avisos: ['Nao encontrei nenhum dos tres valores no texto colado. Confirma que copiaste os cartoes todos, ou escreve os numeros a mao.'],
    }
  }

  // Uma etiqueta que nao aparece vale zero: o cartao do SOLICITADO so existe no
  // painel quando ha algum pedido em curso. Nao e um erro, e nao vale a pena
  // avisar de uma coisa que e o estado normal.
  const total = Math.round(((pendente || 0) + (disponivel || 0) + (solicitado || 0)) * 100) / 100

  if (totalDeclarado != null && Math.abs(totalDeclarado - total) > 0.01) {
    avisos.push(
      `O painel diz ${totalDeclarado.toFixed(2)} de total, mas as parcelas que li somam ${total.toFixed(2)}. ` +
      'Falta-me uma parcela — confere antes de gravar.')
  }

  return { ok: true, pendente: pendente || 0, disponivel: disponivel || 0, solicitado: solicitado || 0, total, totalDeclarado, avisos }
}
